import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const FACT_COLOR = "#8aa0b8";
const CHART_COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#ea580c",
  "#0891b2",
  "#be185d",
  "#ca8a04",
  "#4f46e5",
  "#059669",
  "#c2410c",
  "#7c3aed",
  "#0d9488",
  "#db2777",
  "#65a30d",
  "#0369a1",
  "#b45309",
  "#6d28d9",
  "#047857",
  "#9f1239",
];

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("ru-RU", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

function formatPct(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toLocaleString("ru-RU", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

function formatMinutes(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toLocaleString("ru-RU", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })} мин`;
}

function formatDay(value) {
  if (!value) return "";
  const [, month, day] = String(value).slice(0, 10).split("-");
  return day && month ? `${day}.${month}` : value;
}

function eachIsoDay(from, to) {
  const days = [];
  if (!from || !to || from > to) return days;
  const current = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    const day = String(current.getDate()).padStart(2, "0");
    days.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function MetricCard({ label, value, hint }) {
  return (
    <article className="dash-metric">
      <p className="dash-metric-label">{label}</p>
      <p className="dash-metric-value">{value}</p>
      {hint ? <p className="dash-metric-hint">{hint}</p> : null}
    </article>
  );
}

function PeriodTooltip({ active, payload, label, formatValue }) {
  if (!active) return null;
  const items = (payload || []).filter((item) => item?.value != null);
  return (
    <div className="dash-chart-tooltip">
      <p className="dash-chart-tooltip-date">{label}</p>
      {items.length ? (
        items.map((item) => (
          <p key={item.dataKey}>
            {item.name ? `${item.name}: ` : null}
            <strong>{formatValue(item.value)}</strong>
          </p>
        ))
      ) : (
        <p>
          <strong>нет отчёта</strong>
        </p>
      )}
    </div>
  );
}

function restaurantKey(id) {
  return `r${id}`;
}

function RestaurantChartFilters({ items, colors, visibleIds, onToggle, onSetAll }) {
  if (!items.length) return null;
  return (
    <section className="dash-panel">
      <div className="dash-panel-head">
        <h2>Рестораны на графиках</h2>
        <div className="z-report-chart-actions">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onSetAll(true)}
          >
            Включить все
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onSetAll(false)}
          >
            Выключить все
          </button>
        </div>
      </div>
      <div className="z-report-chart-filters" style={{ marginBottom: 0 }}>
        {items.map((item) => {
          const isVisible = visibleIds[item.id];
          return (
            <label
              key={item.id}
              className={`z-report-chart-filter${isVisible ? " is-active" : ""}`}
              style={{ "--filter-color": colors[item.id] }}
            >
              <input
                type="checkbox"
                checked={Boolean(isVisible)}
                onChange={() => onToggle(item.id)}
              />
              <span className="z-report-chart-filter-dot" />
              <span className="z-report-chart-filter-name">{item.name}</span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

function RestaurantPeriodChart({ title, rows, colors, dataKey, formatValue }) {
  const series = (rows || []).map((row) => ({
    id: row.id,
    key: restaurantKey(row.id),
    name: row.name,
    color: colors[row.id] || CHART_COLORS[0],
    byDate: Object.fromEntries((row.days || []).map((day) => [day.date, day])),
  }));
  const dates = [
    ...new Set(
      series.flatMap((item) =>
        Object.entries(item.byDate)
          .filter(([, day]) => day[dataKey] != null && day[dataKey] !== "")
          .map(([date]) => date),
      ),
    ),
  ].sort();
  if (!dates.length || !series.length) return null;

  const data = dates.map((date) => {
    const point = { date, label: formatDay(date) };
    series.forEach((item) => {
      const raw = item.byDate[date]?.[dataKey];
      point[item.key] =
        raw == null || raw === "" ? undefined : Number(raw);
    });
    return point;
  });

  return (
    <section className="dash-panel dash-ops-rest-chart">
      <div className="dash-panel-head">
        <h2>{title}</h2>
        <span className="dash-panel-total">по ресторанам</span>
      </div>
      <div className="dash-ops-rest-chart-wrap">
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={formatValue}
              width={64}
              allowDecimals
            />
            <Tooltip
              content={(props) => (
                <PeriodTooltip {...props} formatValue={formatValue} />
              )}
            />
            {series
              .filter((item) =>
                data.some((point) => point[item.key] != null),
              )
              .map((item) => (
                <Line
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.name}
                  stroke={item.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function PeriodChart({
  title,
  dateFrom,
  dateTo,
  series,
  dataKey,
  formatValue,
  latest,
}) {
  const byDate = Object.fromEntries((series || []).map((item) => [item.date, item]));
  const data = eachIsoDay(dateFrom, dateTo).map((date) => {
    const row = byDate[date];
    const raw = row ? row[dataKey] : null;
    return {
      date,
      label: formatDay(date),
      value: raw == null || raw === "" ? null : Number(raw),
    };
  });
  if (!data.length) return null;

  return (
    <section className="dash-panel dash-side-chart">
      <div className="dash-panel-head">
        <h2>{title}</h2>
        {latest != null ? (
          <span className="dash-panel-total">{formatValue(latest)}</span>
        ) : null}
      </div>
      <div className="dash-side-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={formatValue}
              width={64}
              allowDecimals
            />
            <Tooltip
              content={(props) => (
                <PeriodTooltip {...props} formatValue={formatValue} />
              )}
            />
            <Bar
              dataKey="value"
              name={title}
              fill={FACT_COLOR}
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

const OpsDashboard = ({ summary }) => {
  const network = summary.network || {};
  const restaurants = summary.restaurants || [];
  const restaurantDays = summary.restaurant_days || [];
  const series = summary.series || [];
  const oneRestaurant = Boolean(summary.restaurant_id);
  const selectedRestaurant = oneRestaurant
    ? restaurants.find((row) => row.id === summary.restaurant_id) ||
      restaurants[0] ||
      {}
    : null;
  const [visibleIds, setVisibleIds] = useState({});

  useEffect(() => {
    if (!restaurantDays.length) {
      setVisibleIds({});
      return;
    }
    setVisibleIds((prev) => {
      const next = { ...prev };
      restaurantDays.forEach((row) => {
        if (next[row.id] === undefined) next[row.id] = true;
      });
      return next;
    });
  }, [restaurantDays]);

  const restaurantColors = useMemo(() => {
    const colors = {};
    restaurantDays.forEach((row, index) => {
      colors[row.id] = CHART_COLORS[index % CHART_COLORS.length];
    });
    return colors;
  }, [restaurantDays]);

  const visibleRestaurantDays = restaurantDays.filter(
    (row) => visibleIds[row.id],
  );
  const restaurantCharts = [
    { key: "hi", title: "HI", formatValue: formatNumber },
    {
      key: "speed_in_target",
      title: "% заказов в цели",
      formatValue: formatPct,
    },
    {
      key: "speed_service_min",
      title: "Время обслуживания",
      formatValue: formatMinutes,
    },
    { key: "sstg", title: "SSTG", formatValue: formatPct },
    {
      key: "rost_score_pct",
      title: "% оценки РОСТ",
      formatValue: formatPct,
    },
  ];
  const snapshotHint = summary.snapshot_date
    ? `на ${formatDay(summary.snapshot_date)}`
    : "";
  const hasRestaurantDays = restaurantDays.some(
    (row) => (row.days || []).length > 0,
  );

  const charts = [
    {
      key: "hi",
      title: "HI",
      formatValue: formatNumber,
      latest: network.hi,
    },
    {
      key: "speed_in_target",
      title: "% заказов в цели",
      formatValue: formatPct,
      latest: oneRestaurant ? null : network.speed_in_target,
    },
    {
      key: "speed_service_min",
      title: "Время обслуживания",
      formatValue: formatMinutes,
      latest: oneRestaurant ? null : network.speed_service_min,
    },
    {
      key: "sstg",
      title: "SSTG",
      formatValue: formatPct,
      latest: oneRestaurant ? null : network.sstg,
    },
    oneRestaurant
      ? {
          key: "rost_score_pct",
          title: "% оценки РОСТ",
          formatValue: formatPct,
          latest: selectedRestaurant?.rost_score_pct,
        }
      : {
          key: "rost_in_target_pct",
          title: "% РОСТ в цели",
          formatValue: formatPct,
          latest: network.rost_in_target_pct,
        },
  ];
  const rostHint = selectedRestaurant?.rost_date
    ? `визит ${formatDay(selectedRestaurant.rost_date)}`
    : snapshotHint;

  return (
    <div className="dash-ops">
      <section className="dash-metrics">
        <MetricCard
          label="HI"
          value={formatNumber(
            selectedRestaurant ? selectedRestaurant.hi : network.hi,
          )}
          hint={snapshotHint}
        />
        <MetricCard
          label="% заказов в цели"
          value={formatPct(
            selectedRestaurant
              ? selectedRestaurant.speed_in_target
              : network.speed_in_target,
          )}
          hint={snapshotHint}
        />
        <MetricCard
          label="Время обслуживания"
          value={formatMinutes(
            selectedRestaurant
              ? selectedRestaurant.speed_service_min
              : network.speed_service_min,
          )}
          hint={snapshotHint}
        />
        <MetricCard
          label="SSTG"
          value={formatPct(
            selectedRestaurant ? selectedRestaurant.sstg : network.sstg,
          )}
          hint={snapshotHint}
        />
        <MetricCard
          label={selectedRestaurant ? "РОСТ" : "% РОСТ в цели"}
          value={
            selectedRestaurant
              ? selectedRestaurant.rost_grade ||
                selectedRestaurant.rost_score_pct != null
                ? `${selectedRestaurant.rost_grade || "—"} · ${formatPct(
                    selectedRestaurant.rost_score_pct,
                  )}`
                : "—"
              : formatPct(network.rost_in_target_pct)
          }
          hint={selectedRestaurant ? rostHint : snapshotHint}
        />
      </section>

      {oneRestaurant || !hasRestaurantDays ? (
        <div className="dash-ops-charts">
          {charts.map((chart) => (
            <PeriodChart
              key={chart.key}
              title={chart.title}
              dateFrom={summary.date_from}
              dateTo={summary.date_to}
              series={series}
              dataKey={chart.key}
              formatValue={chart.formatValue}
              latest={chart.latest}
            />
          ))}
        </div>
      ) : (
        <div className="dash-ops-rest-charts">
          <RestaurantChartFilters
            items={restaurantDays}
            colors={restaurantColors}
            visibleIds={visibleIds}
            onToggle={(id) =>
              setVisibleIds((prev) => ({ ...prev, [id]: !prev[id] }))
            }
            onSetAll={(value) => {
              const next = {};
              restaurantDays.forEach((row) => {
                next[row.id] = value;
              });
              setVisibleIds(next);
            }}
          />
          {visibleRestaurantDays.length === 0 ? (
            <p className="z-report-chart-empty">
              Выберите хотя бы один ресторан для графика
            </p>
          ) : (
            restaurantCharts.map((chart) => (
              <RestaurantPeriodChart
                key={chart.key}
                title={chart.title}
                rows={visibleRestaurantDays}
                colors={restaurantColors}
                dataKey={chart.key}
                formatValue={chart.formatValue}
              />
            ))
          )}
        </div>
      )}

      {restaurants.length > 0 ? (
        <section className="dash-panel">
          <div className="dash-panel-head">
            <h2>Рестораны</h2>
            <span className="dash-panel-total">
              {snapshotHint || "последний день"}
              , РОСТ — последний визит на дату «по»
            </span>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table dash-table-wide">
              <thead>
                <tr>
                  <th>Ресторан</th>
                  <th className="is-num">HI</th>
                  <th className="is-num">% заказов в цели</th>
                  <th className="is-num">Время, мин</th>
                  <th className="is-num">SSTG</th>
                  <th>РОСТ</th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td className="is-num">{formatNumber(item.hi)}</td>
                    <td className="is-num">{formatPct(item.speed_in_target)}</td>
                    <td className="is-num">{formatNumber(item.speed_service_min)}</td>
                    <td className="is-num">{formatPct(item.sstg)}</td>
                    <td>
                      {item.rost_grade || item.rost_score_pct != null
                        ? `${item.rost_grade || "—"} · ${formatPct(
                            item.rost_score_pct,
                          )}${
                            item.rost_date
                              ? ` · ${formatDay(item.rost_date)}`
                              : ""
                          }`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="dash-legend">
        <h2>Как считаются цифры</h2>
        <p>
          Берём загрузки OPS за выбранные даты
          {summary.loaded_dates?.length
            ? ` (есть отчёты за ${summary.loaded_dates
                .map(formatDay)
                .join(", ")})`
            : ""}
          . День без отчёта на графике пустой. Значения не суммируются.
        </p>
        <dl>
          <div>
            <dt>Сеть</dt>
            <dd>
              Карточки — снимок последнего дня с HI / SPEED / SSTG
              {summary.snapshot_date
                ? ` (${formatDay(summary.snapshot_date)})`
                : ""}
              . Если выбрана одна точка, карточки сети не меняются.
            </dd>
          </div>
          <div>
            <dt>Ресторан</dt>
            <dd>
              Графики — все точки сразу: по горизонтали дата, по вертикали
              значение, линия на ресторан. Чипами сверху можно включить или
              выключить точки, как в графике МНГ. День без числа не
              соединяется.
              Таблица — HI, скорость и SSTG на последний день периода; РОСТ —
              последний визит на дату «по», даже если он раньше начала периода.
              Если выбрана точка, вместо общих линий рисуем её столбцы по дням.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
};

export default OpsDashboard;
